import { useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import { ChevronDown, LibraryBig, Tag } from "lucide-react";
import { toast } from "sonner";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import { Skeleton } from "@/components/ui/skeleton";
import { LibrarySearchPicker } from "@/components/library/LibrarySearchPicker";
import { LibraryCategoryBadge } from "@/components/library/LibraryCategoryBadge";
import { getSourceRowToneVars } from "@/lib/sourceTypeRowTone";
import { getSourceTypeIcon } from "@/lib/sourceTypeIcons";
import {
  fetchLibraryItems,
  getLibrarySourceTypeLabel,
  librarySourceTypeOptions,
  type LibraryItemWithWorkspaces,
  type LibrarySourceType,
} from "@/lib/library";
import {
  fetchLibraryCategories,
  fetchLibraryItemCategoryAssignments,
  type LibraryCategory,
} from "@/lib/libraryCategories";

export const COMPOSER_LIBRARY_DRAG_MIME = "application/x-excerptus-library-ref";

type SourceTypeValue = NonNullable<LibrarySourceType>;

type LibraryRowProps = {
  item: LibraryItemWithWorkspaces;
};

const LibraryRow = ({ item }: LibraryRowProps) => {
  const title = item.display_title ?? item.title;
  const author = item.author_text ?? null;
  const sourceType = item.source_type ?? "other";
  const tone = getSourceRowToneVars(sourceType);
  const SourceIcon = getSourceTypeIcon(sourceType);
  const [hovered, setHovered] = useState(false);

  const handleDragStart = (e: React.DragEvent<HTMLDivElement>) => {
    const payload = JSON.stringify({
      kind: "library",
      id: item.id,
      title: title ?? "",
      author: author ?? "",
      source_type: item.source_type ?? null,
      isbn_10: item.isbn_10 ?? null,
      isbn_13: item.isbn_13 ?? null,
      published_year: item.published_year ?? null,
    });
    e.dataTransfer.setData(COMPOSER_LIBRARY_DRAG_MIME, payload);
    e.dataTransfer.setData("text/plain", title ?? "");
    e.dataTransfer.effectAllowed = "copy";
  };

  return (
    <div
      role="listitem"
      draggable
      onDragStart={handleDragStart}
      onMouseEnter={() => setHovered(true)}
      onMouseLeave={() => setHovered(false)}
      title="Drag into the Draft Canvas to insert as a quote."
      style={{
        backgroundColor: hovered ? tone.hover : tone.idle,
        borderLeft: `4px solid ${tone.accent}`,
      }}
      className="flex w-full items-center gap-3 rounded-md p-2 text-left select-none cursor-grab active:cursor-grabbing transition-colors"
    >
      <div
        draggable={false}
        className="flex h-8 w-8 shrink-0 items-center justify-center rounded-md pointer-events-none"
        style={{ backgroundColor: tone.selected, color: tone.title }}
        aria-hidden="true"
      >
        <SourceIcon className="h-4 w-4" />
      </div>
      <div draggable={false} className="min-w-0 flex-1 pointer-events-none">
        <div className="truncate text-sm font-medium" style={{ color: tone.title }}>{title}</div>
        <div className="truncate text-xs" style={{ color: tone.meta }}>
          {author ?? "Unknown author"}
          {(() => {
            const year = item.published_year
              ? String(item.published_year)
              : item.published_date?.match(/\d{4}/)?.[0] ?? null;
            return year ? <> · {year}</> : null;
          })()}
          {" "}
          <span
            className="ml-1 inline-flex items-center rounded-full px-1.5 py-0 align-middle text-[10px] font-semibold"
            style={{ color: tone.title, borderColor: tone.selected, borderWidth: 1, backgroundColor: "transparent" }}
          >
            {getLibrarySourceTypeLabel(sourceType)}
          </span>
        </div>
      </div>
    </div>
  );
};

export type ComposerLibraryPickerProps = {
  workspaceId: string | undefined;
};

const matchesQuery = (item: LibraryItemWithWorkspaces, q: string) => {
  if (!q) return true;
  const needle = q.trim().toLowerCase();
  if (!needle) return true;
  const haystack = [
    item.display_title,
    item.title,
    item.subtitle,
    item.author_text,
    item.isbn_10,
    item.isbn_13,
    item.publisher,
  ]
    .filter(Boolean)
    .join(" ")
    .toLowerCase();
  return haystack.includes(needle);
};

export const ComposerLibraryPicker = ({ workspaceId }: ComposerLibraryPickerProps) => {
  const [items, setItems] = useState<LibraryItemWithWorkspaces[]>([]);
  const [categories, setCategories] = useState<LibraryCategory[]>([]);
  const [assignmentsByItemId, setAssignmentsByItemId] = useState<Map<string, Set<string>>>(new Map());
  const [loading, setLoading] = useState(true);
  const [query, setQuery] = useState("");
  const [selectedSourceTypes, setSelectedSourceTypes] = useState<SourceTypeValue[]>([]);
  const [selectedCategoryIds, setSelectedCategoryIds] = useState<string[]>([]);

  useEffect(() => {
    let cancelled = false;
    if (!workspaceId) {
      setItems([]);
      setCategories([]);
      setAssignmentsByItemId(new Map());
      setLoading(false);
      return;
    }
    setLoading(true);
    (async () => {
      try {
        const [rows, cats] = await Promise.all([
          fetchLibraryItems(workspaceId),
          fetchLibraryCategories(workspaceId),
        ]);
        const assignments = await fetchLibraryItemCategoryAssignments(rows.map((r) => r.id));
        if (cancelled) return;
        const map = new Map<string, Set<string>>();
        for (const a of assignments) {
          const set = map.get(a.library_item_id) ?? new Set<string>();
          set.add(a.library_category_id);
          map.set(a.library_item_id, set);
        }
        setItems(rows);
        setCategories(cats);
        setAssignmentsByItemId(map);
      } catch (err) {
        if (!cancelled) toast.error((err as Error).message);
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [workspaceId]);

  const toggleSourceType = (value: SourceTypeValue) =>
    setSelectedSourceTypes((prev) =>
      prev.includes(value) ? prev.filter((v) => v !== value) : [...prev, value],
    );
  const toggleCategory = (id: string) =>
    setSelectedCategoryIds((prev) => (prev.includes(id) ? prev.filter((v) => v !== id) : [...prev, id]));

  const hasFilters =
    query.trim().length > 0 || selectedSourceTypes.length > 0 || selectedCategoryIds.length > 0;

  const filtered = useMemo(
    () =>
      items.filter((item) => {
        if (!matchesQuery(item, query)) return false;
        if (selectedSourceTypes.length > 0) {
          const st = (item.source_type ?? "other") as SourceTypeValue;
          if (!selectedSourceTypes.includes(st)) return false;
        }
        if (selectedCategoryIds.length > 0) {
          const assigned = assignmentsByItemId.get(item.id);
          if (!assigned || !selectedCategoryIds.some((id) => assigned.has(id))) return false;
        }
        return true;
      }),
    [items, query, selectedSourceTypes, selectedCategoryIds, assignmentsByItemId],
  );

  return (
    <div className="flex h-full flex-col gap-3">
      <div className="flex items-center gap-2">
        <LibraryBig className="h-4 w-4 text-muted-foreground" />
        <span className="text-sm font-medium">My Library</span>
        <Badge variant="outline" className="ml-auto text-[10px] uppercase tracking-wider">
          {items.length}
        </Badge>
      </div>

      <div className="flex flex-wrap items-center gap-2">
        <Popover>
          <PopoverTrigger asChild>
            <Button variant="outline" size="sm" className="gap-2">
              <span>Source type</span>
              {selectedSourceTypes.length > 0 ? (
                <span className="inline-flex items-center justify-center rounded-md bg-secondary px-1.5 py-0 text-[10px] font-semibold text-secondary-foreground">
                  {selectedSourceTypes.length}
                </span>
              ) : null}
              <ChevronDown className="h-3.5 w-3.5 opacity-60" />
            </Button>
          </PopoverTrigger>
          <PopoverContent align="start" className="w-64 space-y-3">
            <div className="flex items-center justify-between">
              <p className="text-sm font-medium text-foreground">Filter by source type</p>
              {selectedSourceTypes.length > 0 ? (
                <Button variant="ghost" size="sm" className="h-7 px-2 text-xs" onClick={() => setSelectedSourceTypes([])}>
                  Clear
                </Button>
              ) : null}
            </div>
            <div className="flex flex-wrap gap-2">
              {librarySourceTypeOptions.map((option) => (
                <Button
                  key={option.value}
                  type="button"
                  variant={selectedSourceTypes.includes(option.value as SourceTypeValue) ? "secondary" : "outline"}
                  size="sm"
                  onClick={() => toggleSourceType(option.value as SourceTypeValue)}
                >
                  {option.label}
                </Button>
              ))}
            </div>
          </PopoverContent>
        </Popover>

        {categories.length > 0 ? (
          <Popover>
            <PopoverTrigger asChild>
              <Button variant="outline" size="sm" className="gap-2">
                <Tag className="h-4 w-4 text-primary" />
                <span>Categories</span>
                {selectedCategoryIds.length > 0 ? (
                  <span className="inline-flex items-center justify-center rounded-md bg-secondary px-1.5 py-0 text-[10px] font-semibold text-secondary-foreground">
                    {selectedCategoryIds.length}
                  </span>
                ) : null}
                <ChevronDown className="h-3.5 w-3.5 opacity-60" />
              </Button>
            </PopoverTrigger>
            <PopoverContent align="start" className="w-72 space-y-3">
              <div className="flex items-center justify-between">
                <p className="text-sm font-medium text-foreground">Filter by category</p>
                {selectedCategoryIds.length > 0 ? (
                  <Button variant="ghost" size="sm" className="h-7 px-2 text-xs" onClick={() => setSelectedCategoryIds([])}>
                    Clear
                  </Button>
                ) : null}
              </div>
              <div className="flex flex-wrap gap-2">
                {categories.map((category) => (
                  <LibraryCategoryBadge
                    key={category.id}
                    category={category}
                    selected={selectedCategoryIds.includes(category.id)}
                    onClick={() => toggleCategory(category.id)}
                  />
                ))}
              </div>
            </PopoverContent>
          </Popover>
        ) : null}
      </div>

      <LibrarySearchPicker value={query} onChange={setQuery} />

      <div className="min-h-0 flex-1 overflow-auto">
        {loading ? (
          <div className="space-y-2">
            {Array.from({ length: 4 }).map((_, i) => (
              <Skeleton key={i} className="h-14 w-full" />
            ))}
          </div>
        ) : !workspaceId ? (
          <p className="py-6 text-center text-xs text-muted-foreground">
            Pick a workspace to browse its library.
          </p>
        ) : filtered.length === 0 ? (
          <div className="flex h-full flex-col items-center justify-center gap-2 px-4 text-center">
            <p className="text-sm text-muted-foreground">
              {items.length === 0
                ? "No saved library items yet."
                : hasFilters
                  ? "No items match your filters."
                  : "No items match your search."}
            </p>
            {items.length === 0 && (
              <Button asChild size="sm" variant="outline">
                <Link to="/my-library">Open My Library</Link>
              </Button>
            )}
          </div>
        ) : (
          <ul className="divide-y divide-border/60">
            {filtered.map((item) => (
              <li key={item.id}>
                <LibraryRow item={item} />
              </li>
            ))}
          </ul>
        )}
      </div>

      <div className="border-t border-border/60 pt-2 text-right">
        <Button asChild size="sm" variant="ghost" className="h-7 text-xs">
          <Link to="/my-library">Manage My Library →</Link>
        </Button>
      </div>
    </div>
  );
};
